import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo, useState, type ReactNode } from 'react'
import { allMonthCalendarQueries } from '@opallabs/api/BFF'
import { toast } from '@opallabs/lapidary'
import {
  CalendarMenuActionIds,
  DeleteItemDialog,
  RenameItemDialog,
  type CalendarItemAction,
  type CalendarMenuCapabilities,
} from '../menu'
import {
  deleteContent,
  deleteMoment,
  duplicateContent,
  renameContent,
  renameMoment,
} from '../mutations/itemActions'
import { type CalendarItem } from '../types'

/**
 * The actions the month view cannot carry out on its own.
 *
 * Most open a modal that belongs to the page around the calendar and work on a
 * Backbone model, so the calendar hands the item over and hears no more about
 * it. An action whose callback is missing is left off the menu rather than
 * offered and ignored — which is also how the host applies a feature flag it
 * knows about and the calendar does not.
 */
export type CalendarHostActions = Readonly<{
  /**
   * Opens the board picker. Unschedule is the same modal with its unschedule
   * box already ticked, which is what the week view's two menu entries do.
   */
  onAddToBoard?: (
    item: CalendarItem,
    options: { shouldUnschedule: boolean }
  ) => void
  onAddToPlan?: (item: CalendarItem) => void
  onDuplicateMoment?: (item: CalendarItem) => void
  /**
   * Opens the in-market preview of a stretch of days. Only the host knows where
   * that page is, and a day cell offers the button only when it can be shown.
   */
  onPreviewRange?: (start: Date, end: Date) => void
}>

/**
 * Answers what a person picked from an item's menu.
 *
 * Three of the actions belong to the host and leave through `hostActions`. The
 * rest are requests this view makes itself, and each ends by asking the month
 * again: a rename can change how an item sorts, a duplicate adds one, and a
 * deleted moment takes its content with it, so nothing here is worth writing
 * into the cache by hand.
 */
export const useCalendarItemActions = ({
  canEdit = true,
  hostActions,
  workspaceUuid,
}: {
  /** Whether this reader may change an item at all. */
  canEdit?: boolean
  hostActions?: CalendarHostActions
  workspaceUuid: string
}): {
  capabilities: CalendarMenuCapabilities
  onItemAction: (action: CalendarItemAction) => void
  renderDialogs: () => ReactNode
} => {
  const queryClient = useQueryClient()
  const [itemToRename, setItemToRename] = useState<CalendarItem | null>(null)
  const [itemToDelete, setItemToDelete] = useState<CalendarItem | null>(null)

  const refresh = useCallback(
    () =>
      allMonthCalendarQueries.invalidateCache(queryClient, { workspaceUuid }),
    [queryClient, workspaceUuid]
  )

  const rename = useMutation({
    mutationFn: async ({
      item,
      name,
    }: {
      item: CalendarItem
      name: string
    }) => {
      if (item.kind === 'moment') {
        await renameMoment({ momentId: item.id, title: name })
        return
      }
      await renameContent({ item, name })
    },
    onError: () => {
      toast('That could not be renamed. Please try again.', { type: 'error' })
    },
    onSuccess: () => {
      setItemToRename(null)
    },
    onSettled: refresh,
  })

  const duplicate = useMutation({
    mutationFn: (item: CalendarItem) => duplicateContent({ item }),
    onError: () => {
      toast('That could not be duplicated. Please try again.', {
        type: 'error',
      })
    },
    onSuccess: refresh,
  })

  const remove = useMutation({
    mutationFn: async (item: CalendarItem) => {
      if (item.kind === 'moment') {
        await deleteMoment({ momentId: item.id })
        return
      }
      await deleteContent({ item })
    },
    onError: () => {
      toast('That could not be deleted. Please try again.', { type: 'error' })
    },
    onSettled: () => {
      setItemToDelete(null)
      refresh()
    },
  })

  const { mutate: duplicateContentItem } = duplicate

  const capabilities = useMemo(
    () => ({
      canAddToBoard: hostActions?.onAddToBoard !== undefined,
      canAddToPlan: hostActions?.onAddToPlan !== undefined,
      canDuplicateMoment: hostActions?.onDuplicateMoment !== undefined,
      canEdit,
    }),
    [canEdit, hostActions]
  )

  const onItemAction = useCallback(
    ({ id, item }: CalendarItemAction) => {
      switch (id) {
        case CalendarMenuActionIds.openInNewTab:
          if (item.href !== undefined) window.open(item.href, '_blank')
          return
        case CalendarMenuActionIds.copyLink:
          if (item.href !== undefined) {
            navigator.clipboard
              .writeText(new URL(item.href, window.location.origin).toString())
              .then(
                () => toast('Link copied.', { type: 'success' }),
                () => toast('That link could not be copied.', { type: 'error' })
              )
          }
          return
        case CalendarMenuActionIds.rename:
          setItemToRename(item)
          return
        case CalendarMenuActionIds.duplicate:
          // A moment is duplicated by the host, which asks what to bring along.
          if (item.kind === 'moment') hostActions?.onDuplicateMoment?.(item)
          else duplicateContentItem(item)
          return
        case CalendarMenuActionIds.addToPlan:
          hostActions?.onAddToPlan?.(item)
          return
        case CalendarMenuActionIds.addToBoard:
          hostActions?.onAddToBoard?.(item, { shouldUnschedule: false })
          return
        case CalendarMenuActionIds.unschedule:
          hostActions?.onAddToBoard?.(item, { shouldUnschedule: true })
          return
        case CalendarMenuActionIds.delete:
          setItemToDelete(item)
      }
    },
    [duplicateContentItem, hostActions]
  )

  const renderDialogs = useCallback(
    () => (
      <>
        <RenameItemDialog
          isSaving={rename.isLoading}
          item={itemToRename}
          onCancel={() => setItemToRename(null)}
          onRename={(name) =>
            itemToRename && rename.mutate({ item: itemToRename, name })
          }
        />
        <DeleteItemDialog
          isDeleting={remove.isLoading}
          item={itemToDelete}
          onCancel={() => setItemToDelete(null)}
          onConfirm={() => itemToDelete && remove.mutate(itemToDelete)}
        />
      </>
    ),
    [itemToDelete, itemToRename, remove, rename]
  )

  return { capabilities, onItemAction, renderDialogs }
}
